import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { DEFAULT_LANG, STORAGE_KEY, type LangCode } from "./config";
import { detectBrowserLang, readStoredLang, resolveLanguage } from "./detect";
import { UI, UI_EN, type UIKey } from "./ui";
import { ITEM_TRANSLATIONS, type ItemFields } from "./items";
import { getRuntimeTranslation, getTranslationsVersion, reconcileCacheVersion, subscribeToTranslations } from "./runtime-translate";
import { supabase } from "@/integrations/supabase/client";
import { getUiOverrides } from "@/lib/ui-overrides.functions";
import type { Item } from "@/data/places";

interface LanguageCtx {
  lang: LangCode;
  setLang: (l: LangCode) => void;
  /** Translate a UI key. Falls back to English. */
  t: (key: UIKey) => string;
  /**
   * Translate a single field on a place item.
   * Falls back to the original english value on the item.
   */
  tItem: <K extends keyof ItemFields & keyof Item>(item: Item, field: K) => string;
}

const Ctx = createContext<LanguageCtx | null>(null);
const overridesMemo: Record<string, Record<string, string>> = {};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<LangCode>(DEFAULT_LANG);
  const [hydrated, setHydrated] = useState(false);

  // Resolve the language on first client paint with the documented fallback
  // order: URL prefix → ?lang= → stored preference → browser locale → English.
  // (SSR always renders DEFAULT_LANG, so this cannot cause a hydration diff.)
  useEffect(() => {
    const { lang: detected, source } = resolveLanguage({
      pathname: window.location.pathname,
      search: window.location.search,
      stored: readStoredLang(STORAGE_KEY),
      browser: detectBrowserLang(),
    });
    setLangState(detected);
    if (source === "browser" || source === "query") {
      try { localStorage.setItem(STORAGE_KEY, detected); } catch { /* noop */ }
    }
    setHydrated(true);
  }, []);


  // Reconcile client-side translation cache against the server's
  // `translation_cache_meta.version`. When a scheduled job (or admin edit)
  // bumps that number, every browser wipes its localStorage translation cache
  // on next load so freshly-translated text propagates to all languages.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data, error } = await supabase
          .from("translation_cache_meta")
          .select("version")
          .eq("id", 1)
          .maybeSingle();
        if (cancelled || error || !data) return;
        const v = Number((data as { version: number | string }).version);
        if (Number.isFinite(v)) reconcileCacheVersion(v);
      } catch { /* offline / first load — fine */ }
    };
    void check();
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Keep <html lang> in sync for a11y / SEO.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((l: LangCode) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* noop */ }
  }, []);

  // The `/fi/*` URL tree always renders Finnish, regardless of the stored
  // preference. The path is known during SSR too, so server and client agree.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pathLang: LangCode | null = /^\/fi(\/|$)/.test(pathname) ? "fi" : null;

  // Until after first client paint, always render with DEFAULT_LANG so the
  // SSR HTML and the initial client render match — avoids React hydration errors.
  const activeLang: LangCode = pathLang ?? (hydrated ? lang : DEFAULT_LANG);

  // Re-render when runtime AI translations arrive.
  useSyncExternalStore(
    subscribeToTranslations,
    getTranslationsVersion,
    () => 0, // SSR snapshot
  );

  // Admin-edited overrides for static dictionary strings (Finnish Translations
  // → Interface Copy). Keyed by the English source text.
  const [overrides, setOverrides] = useState<Record<string, string>>(() => overridesMemo[activeLang] ?? {});
  useEffect(() => {
    if (activeLang === "en") { setOverrides({}); return; }
    const cached = overridesMemo[activeLang];
    if (cached) setOverrides(cached);
    let cancelled = false;
    void (async () => {
      try {
        const res = await getUiOverrides({
          data: { lang: activeLang, sources: Array.from(new Set(Object.values(UI_EN))) },
        });
        if (!cancelled && res?.overrides) {
          overridesMemo[activeLang] = res.overrides;
          setOverrides(res.overrides);
        }
      } catch { /* keep built-in dictionary */ }
    })();
    return () => { cancelled = true; };
  }, [activeLang]);

  const t = useCallback(
    (key: UIKey): string => {
      const english = UI_EN[key] ?? String(key);
      const override = overrides[english];
      if (typeof override === "string" && override.length > 0) return override;
      const direct = UI[activeLang]?.[key];
      if (typeof direct === "string" && direct.length > 0) return direct;
      const runtime = getRuntimeTranslation(activeLang, key, english);
      return runtime ?? english;
    },
    [activeLang, overrides],
  );

  const tItem = useCallback(
    <K extends keyof ItemFields & keyof Item>(item: Item, field: K): string => {
      const dict = ITEM_TRANSLATIONS[activeLang];
      const translated = dict?.[item.id]?.[field];
      if (typeof translated === "string" && translated.length > 0) return translated;
      const original = item[field];
      return typeof original === "string" ? original : "";
    },
    [activeLang],
  );

  const value = useMemo<LanguageCtx>(() => ({ lang: activeLang, setLang, t, tItem }), [activeLang, setLang, t, tItem]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLanguage() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
}
